import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { CheckboxItem } from './Inputs';
import { useCardStyle } from './styled';
import { SignInForm } from './types';
import * as assets from '@assets';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import {
  Card,
  Container,
  Divider,
  Grid,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { RemoveRedEye, VisibilityOff } from '@material-ui/icons';

import useUserActionLog from '@hooks/useUserActionLog';
import { emailRegex } from '@utils/regex';

interface Props {
  onSubmit: (_: SignInForm) => void;
  onSignUp: () => void;
  onResetPassword: () => void;
}

const SignInPresenter: React.FC<Props> = ({
  onSubmit,
  onSignUp,
  onResetPassword
}: Props) => {
  const { handleSubmit, errors, control } = useForm<SignInForm>({
    defaultValues: { isKeepLoggedIn: ['true'] }
  });
  const history = useHistory();
  const classes = useCardStyle();
  const emailError = errors && errors['email'] ? true : false;
  const passwordError = errors && errors['password'] ? true : false;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const [isVisibilityPassword, setIsVisibilityPassword] = useState(false);
  const { insertButtonLog } = useUserActionLog();

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyItems="space-between"
      height="100vh"
      paddingTop={'64px'}
      paddingBottom={matches ? '20px' : '48px'}
    >
      <Container
        maxWidth="xs"
        classes={{ maxWidthXs: classes.containerXs }}
        disableGutters={true}
      >
        <Box>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            className={classes.logoRoot}
          >
            <img
              style={{ cursor: 'pointer' }}
              src={assets.LOGO_LARGE}
              width="125px"
              height="32px"
              alt=""
              onClick={() => history.push('/')}
            />
          </Grid>

          <Card
            elevation={0}
            className={classes.root}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              className={classes.cardTitle}
            >
              <CustomizedTypography
                variant="h4"
                color="textPrimary"
                fontWeight="700"
              >
                캐파 로그인
              </CustomizedTypography>
            </Grid>

            <Grid
              container
              justifyContent="flex-start"
              direction="column"
              className={classes.textfieldGridContainer}
            >
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                fontWeight="normal"
                gutterBottoms={8}
              >
                이메일 *
              </CustomizedTypography>
              <Controller
                name="email"
                defaultValue=""
                control={control}
                rules={{
                  required: { value: true, message: '이메일을 입력해 주세요.' },
                  pattern: {
                    value: emailRegex,
                    message: '올바른 이메일 형식으로 입력해주세요.'
                  },
                  maxLength: 60
                }}
                render={(renderProps) => {
                  return (
                    <TextField
                      error={emailError}
                      helperText={errors['email']?.message}
                      type="email"
                      InputProps={{
                        onChange: (e) => {
                          e.target.value = e.target.value.replaceAll(' ', '');
                          renderProps.onChange(e);
                        },
                        classes: {
                          notchedOutline: classes.notchedOutline,
                          input: classes.textfieldInputRoot
                        }
                      }}
                      placeholder="이메일"
                      variant="outlined"
                      size="small"
                      {...renderProps}
                    />
                  );
                }}
              />
            </Grid>

            <Grid
              container
              justifyContent="flex-start"
              direction="column"
              className={classes.textfieldGridContainer}
            >
              <CustomizedTypography
                variant="body1"
                color="textPrimary"
                fontWeight="normal"
                gutterBottoms={8}
              >
                비밀번호 *
              </CustomizedTypography>
              <Controller
                defaultValue=""
                name="password"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: '비밀번호를 입력해 주세요.'
                  }
                }}
                render={(renderProps) => {
                  return (
                    <TextField
                      type={isVisibilityPassword ? 'text' : 'password'}
                      error={passwordError}
                      InputProps={{
                        classes: {
                          notchedOutline: classes.notchedOutline,
                          input: classes.textfieldInputRoot
                        },
                        endAdornment: (
                          <InputAdornment
                            className="remove-red-eye-icon"
                            position="end"
                            onClick={() => {
                              insertButtonLog('Button_Login_ShowPassword');
                              setIsVisibilityPassword((state) => !state);
                            }}
                          >
                            <IconButton size="small" edge="end">
                              {isVisibilityPassword ? (
                                <RemoveRedEye />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      placeholder="비밀번호"
                      helperText={errors['password']?.message}
                      variant="outlined"
                      size="small"
                      {...renderProps}
                    />
                  );
                }}
              />
            </Grid>

            <CustomizedButton
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disableElevation
              style={{ marginBottom: 8 }}
            >
              로그인
            </CustomizedButton>

            <Grid
              container
              justifyContent="flex-start"
              alignItems="center"
              style={{ marginLeft: '11px' }}
              className={classes.checkboxContainer}
            >
              <Controller
                name="isKeepLoggedIn"
                control={control}
                render={(controlProps) => {
                  const options = [
                    {
                      label: '로그인 상태 유지',
                      value: 'true'
                    }
                  ];
                  return (
                    <CheckboxItem
                      xs={12}
                      errors={errors}
                      name={controlProps.name}
                      value={controlProps.value}
                      onChange={controlProps.onChange}
                      gridContainerProps={{ justify: 'flex-end' }}
                      options={options}
                    />
                  );
                }}
              />
            </Grid>
          </Card>

          <Grid
            container
            justifyContent="center"
            alignItems="center"
            className={classes.subButtonGridContainer}
          >
            <CustomizedButton
              variant="text"
              color="default"
              onClick={onResetPassword}
              size="large"
            >
              비밀번호 재설정
            </CustomizedButton>

            <Divider
              orientation="vertical"
              className={classes.verticalDivider}
            />

            <CustomizedButton
              variant="text"
              color="default"
              onClick={onSignUp}
              size="large"
            >
              회원가입
            </CustomizedButton>
          </Grid>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center">
          {!matches && <Divider className={classes.divider} />}
          <img
            src={assets.LOGO_MEDIUM_GRAY}
            height={24}
            alt="grayLogo"
            className={classes.grayLogo}
          />
          <CustomizedTypography
            variant="body2"
            color="textSecondary"
            className={classes.infoText}
          >
            한 개의 계정으로 캐파, 캐파 파트너스 서비스를 경험 해보세요.
          </CustomizedTypography>
        </Box>
      </Container>
    </Box>
  );
};

export default SignInPresenter;
